'use client'

import { Suspense, useEffect } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { nowDate } from '@/lib/dates/now'
import { useAnalyticsConsent } from '@/components/shared/analytics-consent-provider'
import {
  buildLandingTouch,
  captureLandingOnce,
  clearStoredTouch,
  decorateSignupHref,
  defaultOwnHosts,
  getLandingTouch,
  landingViewTouch,
  noteViewPath,
  storeTouchIfAbsent,
} from '@/lib/attribution/first-touch'

/** The visitor has signed up or in: the first touch has done its job and is removed. */
function isSignedInPath(pathname: string): boolean {
  return (
    pathname === '/app' ||
    pathname.startsWith('/app/') ||
    pathname === '/onboarding' ||
    pathname.startsWith('/onboarding/')
  )
}

/**
 * Remembers where a visitor first landed (R6.10 AC-1): the external referrer, the landing path and
 * the landing URL's utm/ref, built once per document load and held in module memory. It is written
 * to the device only when `consent === 'accepted'` (outside the EU/EEA/UK that is set automatically;
 * inside, only after "Accept"), cleared the moment consent is rejected, and cleared on the first
 * signed-in page. The sign-up reads it back in `encodeSignupAttribution`. Renders nothing.
 */
function FirstTouchCaptureInner() {
  const { consent } = useAnalyticsConsent()
  const pathname = usePathname()
  const router = useRouter()

  // The document's first view. A module flag, not a ref, so React strict mode's double effect
  // captures once.
  useEffect(() => {
    captureLandingOnce(() => ({
      touch: buildLandingTouch({
        href: window.location.href,
        referrer: document.referrer,
        now: nowDate(),
        ownHosts: [...defaultOwnHosts(), window.location.hostname],
      }),
      pathname: window.location.pathname,
    }))
  }, [])

  // Every pathname change after the landing marks the landing view as left; a signed-in page
  // clears the stored entry.
  useEffect(() => {
    noteViewPath(pathname)
    if (isSignedInPath(pathname)) clearStoredTouch()
  }, [pathname])

  useEffect(() => {
    if (consent === 'rejected') {
      clearStoredTouch()
      return
    }
    if (consent !== 'accepted' || isSignedInPath(window.location.pathname)) return
    const touch = getLandingTouch()
    if (touch) storeTouchIfAbsent(touch)
  }, [consent])

  // While the landing view is current, a click on a sign-up link carries the landing touch in the
  // link's own address (`ft=`). It does not depend on consent: it is what an EU/EEA/UK visitor who
  // has not accepted has instead of storage, and it is harmless for everyone else (a stored touch
  // wins at sign-up). A document capture-phase listener runs before React's root listener, so no
  // CTA `onClick` has run yet when it fires; it never stops propagation, so they all still do, and
  // a `Link` that sees `defaultPrevented` skips its own navigation, leaving ours the only one.
  useEffect(() => {
    if (!landingViewTouch()) return

    function decorate(event: MouseEvent) {
      const touch = landingViewTouch()
      if (!touch || event.defaultPrevented) return
      const target = event.target as Element | null
      const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null | undefined
      if (!anchor) return
      const decorated = decorateSignupHref(
        anchor.getAttribute('href') ?? '',
        touch,
        window.location.origin
      )
      if (!decorated) return

      const opensHere = !anchor.target || anchor.target === '_self'
      const plainLeftClick =
        event.type === 'click' &&
        event.button === 0 &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.shiftKey &&
        !event.altKey &&
        opensHere &&
        !anchor.hasAttribute('download')
      if (plainLeftClick) {
        event.preventDefault()
        router.push(decorated)
      } else {
        // New tab, middle click, modified click: the browser opens the link itself.
        anchor.setAttribute('href', decorated)
      }
    }

    document.addEventListener('click', decorate, true)
    document.addEventListener('auxclick', decorate, true)
    return () => {
      document.removeEventListener('click', decorate, true)
      document.removeEventListener('auxclick', decorate, true)
    }
  }, [pathname, router])

  return null
}

export function FirstTouchCapture() {
  return (
    <Suspense fallback={null}>
      <FirstTouchCaptureInner />
    </Suspense>
  )
}
